import * as React from 'react';
import { useTheme } from '../../theme';
import { CSSRulesFunction } from '../../theme/types';
import { omit } from '../../utils';
import {
  AppearanceProps as BaseAppearanceProps,
  textStyling,
} from './appearance';

type HTMLProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  keyof AppearanceProps
>;

interface AppearanceProps extends BaseAppearanceProps {
  underline?: boolean;
  uppercase?: boolean;
  ellipsis?: boolean;
  capitalise?: boolean;
}

export interface TextProps extends AppearanceProps, HTMLProps {}

const textStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  return [
    {
      ...(props.underline ? { textDecoration: 'underline' } : {}),
      ...(props.uppercase ? { textTransform: 'uppercase' } : {}),
      ...(props.ellipsis
        ? {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }
        : {}),
      ...(props.capitalise
        ? { '&::first-letter': { textTransform: 'capitalize' } }
        : {}),
    },
    textStyling(theme, props),
  ];
};

const getTitle = (props: TextProps): string | undefined => {
  return typeof props.children === 'string' ? props.children : props.title;
};

const Text = React.forwardRef<HTMLSpanElement, TextProps>(function Text(
  props,
  ref
) {
  const { theme } = useTheme();
  const rest = omit(props, [
    'size',
    'color',
    'fontWeight',
    'fontFamily',
    'textAlign',
    'letterSpacing',
    'display',
    'uppercase',
    'underline',
    'ellipsis',
    'title',
    'capitalise',
  ]);
  const title = getTitle(props);

  if (props.ellipsis && !title) {
    // TODO: internal logging utils
    console.warn(
      'Text component with ellipsis either has to have a title or children props that is a string'
    );
  }

  return (
    <span title={title} css={textStyle(theme, props)} {...rest} ref={ref} />
  );
});

export default Text;
